import i18n from './i18n.jsx';
import packageJson from '../../package.json';
import { tx } from '@transifex/native';

import englandFlag from './en/england-flag-icon-256.png';
import latviaFlag from './lv/latvia-flag-icon-256.png';
import russiaFlag from './ru/russia-flag-icon-256.png';
import spainFlag from './es/spain-flag-icon-256.png';
import indiaFlag from './hd/india-flag-icon-256.png';
import franceFlag from './fr/france-flag-icon-256.png';

/**
 * Supported Languages Metadata
 * Target 5 primary languages: lat, eng, rus, esp, india (+ fr preserved)
 */
export const SUPPORTED_LANGUAGES = [
  {
    code: 'en',
    alias: 'eng',
    name: 'English',
    nativeName: 'English',
    flag: englandFlag,
    txLocale: 'en'
  },
  {
    code: 'lv',
    alias: 'lat',
    name: 'Latvian',
    nativeName: 'Latviešu',
    flag: latviaFlag,
    txLocale: 'lv'
  },
  {
    code: 'ru',
    alias: 'rus',
    name: 'Russian',
    nativeName: 'Русский',
    flag: russiaFlag,
    txLocale: 'ru'
  },
  {
    code: 'es',
    alias: 'esp',
    name: 'Spanish',
    nativeName: 'Español',
    flag: spainFlag,
    txLocale: 'es'
  },
  {
    code: 'hd',
    alias: 'india',
    name: 'Hindi',
    nativeName: 'हिन्दी',
    flag: indiaFlag,
    txLocale: 'hi'
  },
  {
    code: 'fr',
    alias: 'fra',
    name: 'French',
    nativeName: 'Français',
    flag: franceFlag,
    txLocale: 'fr'
  }
];

/**
 * Checks whether Transifex Native integration feature is enabled.
 * Defaults to false (disabled). In future, can be enabled via:
 * - Environment variable: REACT_APP_ENABLE_TRANSIFEX=true
 * - package.json config: enableTransifex: "true"
 */
export const isTransifexEnabled = () => {
  return (
    process.env.REACT_APP_ENABLE_TRANSIFEX === 'true' ||
    packageJson?.config?.enableTransifex === 'true'
  );
};

/**
 * Normalizes input language string (code or alias) to canonical internal code.
 * e.g. 'lat' -> 'lv', 'eng' -> 'en', 'india' -> 'hd', 'esp' -> 'es', 'rus' -> 'ru'
 */
export const normalizeLanguageCode = (codeOrAlias) => {
  if (!codeOrAlias) return 'en';
  const clean = String(codeOrAlias).toLowerCase().trim().split('-')[0];

  const matched = SUPPORTED_LANGUAGES.find(
    lang => lang.code.toLowerCase() === clean || lang.alias.toLowerCase() === clean
  );

  if (matched) return matched.code;
  if (clean === 'hi') return 'hd';
  if (clean === 'lat') return 'lv';
  if (clean === 'eng') return 'en';
  if (clean === 'rus') return 'ru';
  if (clean === 'esp') return 'es';
  if (clean === 'india') return 'hd';

  return clean;
};

/**
 * Resolves the Transifex CDS locale code for an internal language code.
 */
export const getTxLocale = (codeOrAlias) => {
  const normalized = normalizeLanguageCode(codeOrAlias);
  const matched = SUPPORTED_LANGUAGES.find(lang => lang.code === normalized);
  return matched ? matched.txLocale : normalized;
};

/**
 * Initializes Transifex Native if feature is enabled, or logs fallback to repo i18n.
 */
export const initTransifex = () => {
  if (isTransifexEnabled()) {
    const token = process.env.REACT_APP_TRANSIFEX_TOKEN;
    if (token) {
      tx.init({
        token,
        cdsHost: process.env.REACT_APP_TRANSIFEX_CDS_HOST || 'https://cds.transifex.com',
      });
      console.info('[Transifex Native] Initialized successfully with CDS host.');
    } else {
      console.warn('[Transifex Native] Enabled but REACT_APP_TRANSIFEX_TOKEN is missing. Utilizing local i18n bundles.');
    }
  } else {
    console.info('[Transifex Native] Feature is currently disabled. Using repository i18n bundles.');
  }
};

/**
 * Centrally switches language across i18next and Transifex (if enabled).
 * Persists user preference to localStorage.
 */
export const changeLanguage = async (codeOrAlias) => {
  const normalizedCode = normalizeLanguageCode(codeOrAlias);
  const txLocale = getTxLocale(codeOrAlias);

  // Update i18next
  if (i18n && typeof i18n.changeLanguage === 'function') {
    await i18n.changeLanguage(normalizedCode);
  }

  // Update Transifex Native if active
  if (isTransifexEnabled() && tx && typeof tx.setCurrentLocale === 'function') {
    try {
      await tx.setCurrentLocale(txLocale);
    } catch (err) {
      console.warn('[Transifex Native] Could not set locale on Transifex CDS:', err);
    }
  }

  // Persist preference
  try {
    localStorage.setItem('i18nextLng', normalizedCode);
  } catch (e) {
    // Ignore storage errors in restricted contexts
  }

  return normalizedCode;
};

/**
 * Returns current language code (normalized).
 */
export const getCurrentLanguage = () => {
  const current = (i18n && i18n.language) || (typeof localStorage !== 'undefined' && localStorage.getItem('i18nextLng')) || 'en';
  return normalizeLanguageCode(current);
};

const transifexService = {
  SUPPORTED_LANGUAGES,
  isTransifexEnabled,
  normalizeLanguageCode,
  getTxLocale,
  initTransifex,
  changeLanguage,
  getCurrentLanguage
};

export default transifexService;

